import { createSelector } from '@ngrx/store';
import { docsFeature } from '../docs/docs.reducer';
import { docKey } from '../docs/docs.normalize';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import {
  ArchitectureDiagram,
  ArchitectureLayout,
  ChatMessage,
  InkDocument,
  RepoContext,
  extractDiagram,
  extractInk,
  extractLayout,
  normalizeArchitectureDiagram,
  normalizeRepoContext,
} from './architecture.models';

const { selectEntities, selectKeyIndex, selectStatusByKey, selectErrorByKey } = docsFeature;

function dataFor(
  entities: ReturnType<typeof selectEntities>,
  keyIndex: Record<string, string>,
  pid: string | null,
  key: string
): any {
  if (!pid) return null;
  const id = keyIndex[docKey(pid, key)];
  return id ? entities.entities[id]?.data ?? null : null;
}

export const selectArchitectureLayout = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): ArchitectureLayout | null => extractLayout(dataFor(e, k, pid, 'architecture.layout'))
);

export const selectArchitectureDiagram = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  selectArchitectureLayout,
  (pid, e, k, layout): ArchitectureDiagram | null => {
    const raw = extractDiagram(dataFor(e, k, pid, 'architecture.main'));
    if (!raw) return null;
    return normalizeArchitectureDiagram(raw, { layout }).diagram;
  }
);

export const selectArchitectureInk = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): InkDocument | null => extractInk(dataFor(e, k, pid, 'architecture.ink'))
);

export const selectArchitectureChatMessages = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): ChatMessage[] => {
    const d = dataFor(e, k, pid, 'architecture.chat');
    return Array.isArray(d?.messages) ? (d.messages as ChatMessage[]) : [];
  }
);

export const selectArchitectureProjectDefinition = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => dataFor(e, k, pid, 'define.definition')
);

export const selectArchitectureRepoContext = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): RepoContext | null => normalizeRepoContext(dataFor(e, k, pid, 'repos.settings'))
);

export const selectArchitectureLoading = createSelector(
  selectActiveProjectId,
  selectStatusByKey,
  (pid, s) => (pid ? s[docKey(pid, 'architecture.main')] === 'loading' : false)
);

export const selectArchitectureError = createSelector(
  selectActiveProjectId,
  selectErrorByKey,
  (pid, e) => (pid ? e[docKey(pid, 'architecture.main')] ?? null : null)
);
